/**
 * =============================================================================
 * CASE DETAILS - PAYLOAD ADAPTERS
 * =============================================================================
 *
 * The case API returns a flat record: admins as `referred_to_admin_name` /
 * `referred_to_admin_phone` pairs, the collection agent as a plain string,
 * logs keyed on `action` + `updatedAt`, doctors as `fullName` + `status`.
 *
 * These adapters turn that record into the shapes the Contact Details, Care
 * Team and Case Log panels render, so the panels stay presentational and every
 * fallback for a missing or differently named field lives in one place.
 * =============================================================================
 */

import moment from "moment";

import { downloadFileFromUrl } from "../ReportCards/reportUtils";
import {
    ANALYTICS_EVENTS,
    REPORT_TYPE,
    logAnalyticsEvent,
} from "../../../analytics";

/* -----------------------------------------------------------------------------
   PRIMITIVES
----------------------------------------------------------------------------- */

const DEFAULT_DIAL_CODE = "+91";

export const cleanName = (value) => {
    if (!value) {
        return "";
    }

    return String(value).replace(/\s+/g, " ").trim();
};

/**
 * The API sends admin phones bare ("9871234563") and agent phones with a
 * country code ("+919871234565"). Both render as "+91 98712 34563".
 *
 * Anything that is not a recognisable number is passed through untouched
 * rather than guessed at.
 */
export const formatPhone = (value) => {
    if (!value) {
        return "";
    }

    const raw = String(value).trim();

    let digits = raw.replace(/\D/g, "");

    /* 00 <country> <local> is the same as + <country> <local> */
    if (digits.startsWith("00")) {
        digits = digits.slice(2);
    }

    if (digits.length < 10) {
        return raw;
    }

    /* Anything past ten digits is the country code. */
    const local = digits.slice(-10);

    const dialCode =
        digits.length > 10
            ? "+" + digits.slice(0, digits.length - 10)
            : DEFAULT_DIAL_CODE;

    return dialCode + " " + local.slice(0, 5) + " " + local.slice(5);
};

export const formatDateTime = (value) => {
    if (!value) {
        return "";
    }

    const parsed = moment(value);

    return parsed.isValid() ? parsed.format("DD/MM/YYYY, hh:mm a") : "";
};

export const getInitials = (name) => {
    const clean = cleanName(name);

    if (!clean) {
        return "";
    }

    return clean
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
};

/* -----------------------------------------------------------------------------
   ACKNOWLEDGMENT DOCUMENTS
----------------------------------------------------------------------------- */

export const formatFileSize = (bytes) => {
    const size = Number(bytes);

    if (!size || Number.isNaN(size)) {
        return "";
    }

    if (size < 1024) {
        return size + " B";
    }

    if (size < 1024 * 1024) {
        return Math.round(size / 1024) + " KB";
    }

    return (size / (1024 * 1024)).toFixed(1) + " MB";
};

/**
 * What each document is called. The backend sends the signed URL and whether the
 * file exists; the wording stays here, with the rest of the UI copy.
 */
const JOURNEY_DOCUMENT_NAMES = {
    report: "Manual Interpreted Report",
    prescription: "Prescription",
};

/**
 * The files reviewed on the acknowledgment step, from the journey payload.
 *
 * Only the ones that actually exist are returned - a case whose prescription was
 * never uploaded shows the report alone rather than a dead row, and a case with
 * neither shows no file list at all.
 */
export const getJourneyDocuments = (caseData, documents = []) =>
    (documents || [])
        .filter((document) => document?.available && document?.url)
        .map((document) => ({
            id: document.id,
            kind: document.kind,
            name:
                JOURNEY_DOCUMENT_NAMES[document.kind] ||
                JOURNEY_DOCUMENT_NAMES[document.id] ||
                "Document",
            url: document.url,
            size: formatFileSize(document.sizeBytes),
            fileName: buildJourneyFileName(caseData, document),
            reportId: caseData?.report_id || null,
        }));

/**
 * Matches how the report screens name a download: "<report id>_<patient>.pdf".
 */
const buildJourneyFileName = (caseData, document) => {
    const name = [caseData?.report_id, cleanName(caseData?.patient_name)]
        .filter(Boolean)
        .join("_")
        .replace(/\s+/g, "_");

    const suffix = document?.kind === "prescription" ? "_prescription" : "";

    return `${name || "report"}${suffix}.pdf`;
};

/**
 * Saves the file rather than opening a tab, using the same downloader the ECG
 * report screens use - blob first, direct link if that is blocked.
 */
export const openJourneyDocument = (file) => {
    if (!file?.url) {
        return;
    }

    logAnalyticsEvent(ANALYTICS_EVENTS.REPORT_DOWNLOADED, {
        report_type:
            file.kind === "prescription"
                ? REPORT_TYPE.PRESCRIPTION
                : REPORT_TYPE.ECG_REPORT,
        report_id: file.reportId,
    });

    downloadFileFromUrl(file.url, file.fileName || "report.pdf");
};

/* -----------------------------------------------------------------------------
   CONTACT DETAILS
----------------------------------------------------------------------------- */

/**
 * A contact reaches us either as an object, as a bare name string, or as a
 * `<name>` / `<phone>` pair of sibling fields on the case.
 */
const toContact = (value, fallbackPhone) => {
    if (!value) {
        return null;
    }

    if (typeof value === "string") {
        const name = cleanName(value);

        return name ? { name, phone: formatPhone(fallbackPhone) } : null;
    }

    const name = cleanName(
        value.fullName ||
            value.full_name ||
            value.name ||
            value.user_name ||
            value.admin_name
    );

    const phone =
        value.phone ||
        value.phoneNumber ||
        value.phone_number ||
        value.mobile ||
        value.mobileNumber ||
        value.admin_phone ||
        fallbackPhone;

    if (!name && !phone) {
        return null;
    }

    return {
        name: name || "--",
        phone: formatPhone(phone),
    };
};

const toContactList = (value, fallbackPhone) => {
    if (!value) {
        return [];
    }

    const list = Array.isArray(value) ? value : [value];

    return list
        .map((item) => toContact(item, fallbackPhone))
        .filter(Boolean);
};

/**
 * The three groups rendered on the Contact Details tab. A group with no
 * contacts is kept so the panel can show the label with a placeholder, exactly
 * like the design.
 */
export const getContactGroups = (caseData) => {
    const hubAdmins = toContactList(
        caseData?.hub_admins ||
            caseData?.hubAdmins ||
            caseData?.referred_to_admins ||
            caseData?.referred_to_admin_name,
        caseData?.referred_to_admin_phone
    );

    const spokeAdmins = toContactList(
        caseData?.spoke_admins ||
            caseData?.spokeAdmins ||
            caseData?.referred_by_admins ||
            caseData?.referred_by_admin_name,
        caseData?.referred_by_admin_phone
    );

    const collectionAgents = toContactList(
        caseData?.collection_agents ||
            caseData?.collectionAgents ||
            caseData?.collection_agent,
        caseData?.collection_agent_phone
    );

    return [
        {
            id: "hub-admin",
            label: "HUB ADMIN",
            contacts: hubAdmins,
        },
        {
            id: "spoke-admin",
            label: "SPOKE ADMIN",
            contacts: spokeAdmins,
        },
        {
            id: "collection-agent",
            label: "COLLECTION AGENT",
            contacts: collectionAgents,
        },
    ];
};

/* -----------------------------------------------------------------------------
   CARE TEAM
----------------------------------------------------------------------------- */

export const DOCTOR_STATUS = {
    accepted: {
        label: "Accepted",
        color: "var(--app-success)",
    },
    approved: {
        label: "Accepted",
        color: "var(--app-success)",
    },
    rejected: {
        label: "Rejected",
        color: "var(--app-danger)",
    },
    declined: {
        label: "Rejected",
        color: "var(--app-danger)",
    },
    pending: {
        label: "Invited",
        color: "var(--app-warning)",
    },
    invited: {
        label: "Invited",
        color: "var(--app-warning)",
    },
};

export const getDoctorStatus = (status) =>
    DOCTOR_STATUS[String(status || "").toLowerCase()] ||
    DOCTOR_STATUS.pending;

/**
 * `caseData.doctors` carries the invite status, the full profile (image, real
 * name) comes from the peoples lookup. This merges the two.
 */
export const getCareTeam = (caseData, peoples = []) => {
    const doctors = Array.isArray(caseData?.doctors)
        ? caseData.doctors
        : [];

    return doctors.map((doctor) => {
        const profile = peoples.find(
            (person) => person._id === doctor._id
        );

        const name = cleanName(
            profile?.fullName || doctor.fullName || "Unknown Doctor"
        );

        return {
            id: doctor._id,
            name,
            displayName: /^dr\.?\s/i.test(name) ? name : "Dr. " + name,
            initials: getInitials(name),
            profileImage:
                profile?.profileImage || doctor.profileImage || "",
            status: doctor.status,
            statusInfo: getDoctorStatus(doctor.status),
        };
    });
};

/* -----------------------------------------------------------------------------
   CASE LOG
----------------------------------------------------------------------------- */

/**
 * Action codes the API sends on `logs[].action`.
 */
const LOG_ACTION_LABELS = {
    CREATED_AT: "Case Created",
    CREATED: "Case Created",
    INVITED_AT: "Doctors Invited",
    INVITED: "Doctors Invited",
    ACCEPTED_AT: "Doctor Accepted",
    ACCEPTED: "Doctor Accepted",
    REJECTED_AT: "Doctor Rejected",
    REJECTED: "Doctor Rejected",
    CONSULTATION_AT: "Consultation",
    CONSULTATION: "Consultation",
    CLOSED_AT: "Case Closed",
    CLOSED: "Case Closed",
    REOPENED_AT: "Case Reopened",
    UPDATED_AT: "Case Updated",
};

/**
 * Unknown codes still read well: "DOCTOR_ASSIGNED_AT" -> "Doctor Assigned".
 */
export const getLogActionLabel = (action) => {
    if (!action) {
        return "";
    }

    const key = String(action).toUpperCase();

    if (LOG_ACTION_LABELS[key]) {
        return LOG_ACTION_LABELS[key];
    }

    return key
        .replace(/_AT$/, "")
        .split("_")
        .filter(Boolean)
        .map(
            (word) =>
                word.charAt(0) + word.slice(1).toLowerCase()
        )
        .join(" ");
};

const isClosingAction = (action) =>
    /CLOSE/i.test(String(action || ""));

/**
 * Normalised, oldest first, ready for the timeline.
 */
export const getCaseLogs = (caseData) => {
    const logs = Array.isArray(caseData?.logs) ? caseData.logs : [];

    return logs
        .map((log, index) => {
            const timestamp =
                log.updatedAt ||
                log.createdAt ||
                log.created_at ||
                log.timestamp ||
                null;

            return {
                id: log._id || log.id || "log-" + index,
                timestamp,
                date: formatDateTime(timestamp),
                sortValue: timestamp
                    ? new Date(timestamp).getTime()
                    : index,
                actionLabel: getLogActionLabel(log.action),
                message: cleanName(
                    log.message ||
                        log.description ||
                        log.event ||
                        ""
                ),
                user: cleanName(
                    log.user_name || log.userName || log.fullName || ""
                ),
                isClosing: isClosingAction(log.action),
            };
        })
        .sort((first, second) => first.sortValue - second.sortValue);
};
